import { useMemo } from 'react'

import { SerializedError } from '@reduxjs/toolkit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'

import { TrackedGame, TrackedGameArchetype } from '../../../../features/live-events/types/TrackedGame'
import { MarketArchetype, useGetTrackedArchetypesQuery } from '../../../api/markets'
import { useTrackedGamesWithAnalystMapping } from './useTrackedGamesAnalysts'

export type MarketBlueprintGameUsage = {
  trackedGame: TrackedGame
  taggedBlueprints: TrackedGameArchetype[]
}

export type MarketBlueprintUsage = {
  marketArchetype: MarketArchetype
  games: MarketBlueprintGameUsage[]
  gameBlueprintCount: number
}

export type GameBlueprintReference = {
  trackedGame: TrackedGame
  blueprint: TrackedGameArchetype
}

export type MarketBlueprintDataIssues = {
  untaggedGameBlueprints: GameBlueprintReference[]
  blueprintsTaggedToDiscontinued: (GameBlueprintReference & { marketArchetype: MarketArchetype })[]
  unusedMarketBlueprints: MarketArchetype[]
}

type MarketBlueprintUsageResult = {
  usages: MarketBlueprintUsage[]
  usageById: { [marketArchetypeId: string]: MarketBlueprintUsage }
  dataIssues: MarketBlueprintDataIssues
  isLoading: boolean
  error?: FetchBaseQueryError | SerializedError
}

/**
 * Combines all Market Level Blueprints with the tracked games list into per-blueprint
 * usage data (which games and game-level blueprints are tagged to each Market Level Blueprint)
 * and derives data quality issues for sanity checking.
 *
 * There is no backend reverse lookup for games by Market Level Blueprint, so the
 * mapping is computed client-side from the embedded archetypes of each tracked game.
 */
export const useMarketBlueprintUsage = (): MarketBlueprintUsageResult => {
  const marketArchetypesQuery = useGetTrackedArchetypesQuery()
  const trackedGamesQuery = useTrackedGamesWithAnalystMapping()

  const marketArchetypes = marketArchetypesQuery.data
  const trackedGames = trackedGamesQuery.data

  return useMemo(() => {
    const gameUsagesByMarketArchetypeId: { [marketArchetypeId: string]: MarketBlueprintGameUsage[] } = {}
    const untaggedGameBlueprints: GameBlueprintReference[] = []
    const marketArchetypeById = (marketArchetypes || []).reduce<{ [id: string]: MarketArchetype }>((acc, marketArchetype) => {
      acc[marketArchetype.id] = marketArchetype
      return acc
    }, {})

    const blueprintsTaggedToDiscontinued: MarketBlueprintDataIssues['blueprintsTaggedToDiscontinued'] = []

    trackedGames?.forEach((trackedGame: TrackedGame) => {
      trackedGame.archetypes?.forEach((blueprint) => {
        if (!blueprint.marketArchetypeId) {
          untaggedGameBlueprints.push({ trackedGame, blueprint })
          return
        }

        const usagesForArchetype = gameUsagesByMarketArchetypeId[blueprint.marketArchetypeId] || []
        const existingGameUsage = usagesForArchetype.find((usage) => usage.trackedGame.game.id === trackedGame.game.id)

        if (existingGameUsage) {
          existingGameUsage.taggedBlueprints.push(blueprint)
        } else {
          usagesForArchetype.push({ trackedGame, taggedBlueprints: [blueprint] })
        }

        gameUsagesByMarketArchetypeId[blueprint.marketArchetypeId] = usagesForArchetype

        const marketArchetype = marketArchetypeById[blueprint.marketArchetypeId]
        if (marketArchetype?.discontinued) {
          blueprintsTaggedToDiscontinued.push({ trackedGame, blueprint, marketArchetype })
        }
      })
    })

    const usages: MarketBlueprintUsage[] = (marketArchetypes || []).map((marketArchetype) => {
      const games = gameUsagesByMarketArchetypeId[marketArchetype.id] || []
      return {
        marketArchetype,
        games,
        gameBlueprintCount: games.reduce((acc, gameUsage) => acc + gameUsage.taggedBlueprints.length, 0),
      }
    })

    const usageById = usages.reduce<{ [marketArchetypeId: string]: MarketBlueprintUsage }>((acc, usage) => {
      acc[usage.marketArchetype.id] = usage
      return acc
    }, {})

    const dataIssues: MarketBlueprintDataIssues = {
      untaggedGameBlueprints,
      blueprintsTaggedToDiscontinued,
      unusedMarketBlueprints: usages.filter((usage) => usage.games.length === 0).map((usage) => usage.marketArchetype),
    }

    return {
      usages,
      usageById,
      dataIssues,
      isLoading: marketArchetypesQuery.isLoading || trackedGamesQuery.isLoading,
      error: marketArchetypesQuery.error,
    }
  }, [marketArchetypes, trackedGames, marketArchetypesQuery.isLoading, marketArchetypesQuery.error, trackedGamesQuery.isLoading])
}
